import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { Product } from '@hygh-vybe/lib';
import { FarmBillStatus } from '@hygh-vybe/lib';
import { API_BASE } from '../../config';

const PAGE_SIZE = 25;

export function AdminProducts() {
  const { authFetch } = useAuth();
  const [searchParams] = useSearchParams();
  const brandFilter = searchParams.get('brand') || '';

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(brandFilter);
  const [searchQuery, setSearchQuery] = useState(brandFilter);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchProducts = useCallback(async (query: string, nextCursor?: string | null) => {
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      // If query matches the brand filter exactly, use brand param for exact match
      if (brandFilter && query === brandFilter) {
        params.set('brand', query);
      } else if (query) {
        params.set('q', query);
      }
      if (nextCursor) params.set('cursor', nextCursor);

      const res = await authFetch(`${API_BASE}/v1/products?${params.toString()}`);
      const json = await res.json();
      if (res.ok && json.data) {
        const items = Array.isArray(json.data) ? json.data : [];
        if (nextCursor) {
          setProducts((prev) => [...prev, ...items]);
        } else {
          setProducts(items);
        }
        setCursor(json.metadata?.pagination?.nextCursor || null);
        setHasMore(!!json.metadata?.pagination?.nextCursor);
        setTotalCount(json.metadata?.pagination?.totalCount || items.length);
      } else {
        setError(json.error?.message || 'Failed to load products');
      }
    } catch {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [authFetch, brandFilter]);

  useEffect(() => {
    setLoading(true);
    fetchProducts(searchQuery);
  }, [searchQuery, fetchProducts]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const handleLoadMore = () => {
    if (cursor) {
      fetchProducts(searchQuery, cursor);
    }
  };

  function getStatusBadge(status: string) {
    switch (status) {
      case FarmBillStatus.Compliant:
        return <span className="admin-badge admin-badge-green">Compliant</span>;
      case FarmBillStatus.NonCompliant:
        return <span className="admin-badge admin-badge-red">Non-Compliant</span>;
      default:
        return <span className="admin-badge admin-badge-yellow">Unverified</span>;
    }
  }

  return (
    <div>
      <div className="admin-top-bar">
        <h1>
          {brandFilter ? `${brandFilter} Products` : 'Products'}
          {' '}
          {totalCount > 0 && <span style={{ fontSize: '0.875rem', fontWeight: 400, color: 'var(--color-text-muted)' }}>({totalCount})</span>}
        </h1>
        <Link to="/admin/products/new" className="admin-btn admin-btn-primary">
          + Add Product
        </Link>
      </div>

      <form className="admin-search-bar" onSubmit={handleSearch}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name, brand, or flavor..."
          className="admin-search-input"
        />
        <button type="submit" className="admin-btn admin-btn-secondary">
          Search
        </button>
        {searchQuery && (
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => { setSearchInput(''); setSearchQuery(''); }}
          >
            Clear
          </button>
        )}
      </form>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading products...</div>
      ) : products.length === 0 && !error ? (
        <div className="admin-placeholder">
          <p>{searchQuery ? 'No products match your search.' : 'No products yet. Add your first product to get started.'}</p>
        </div>
      ) : (
        <>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Brand</th>
                  <th>THC (mg)</th>
                  <th>Format</th>
                  <th>Buy URL</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.productId}>
                    <td>{product.productName}</td>
                    <td>{product.brandName}</td>
                    <td>{product.thcContentMg}</td>
                    <td>{product.beverageFormat}</td>
                    <td>{product.buyUrl ? '✓' : '—'}</td>
                    <td>{getStatusBadge(product.farmBillStatus)}</td>
                    <td>
                      <Link
                        to={`/admin/products/${product.productId}/edit`}
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div style={{ textAlign: 'center', padding: 'var(--spacing-md) 0' }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={handleLoadMore}
              >
                Load More
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
